/**
 * Large portrait illustration shown during playback, synced to the current
 * chapter (companion backend PR: `chapters[].image_url`/`image_thumb_url`).
 * Once a story has any chapter image, this never falls back to the cover:
 * before the first chapter image starts, or for a chapter with none of its
 * own, it keeps showing the nearest chapter image instead — see
 * src/lib/chapterScenes.ts for the pure "which chapter" decision this only
 * renders. The `<Cover>` branch below only ever fires for a story with no
 * chapter image at all.
 *
 * Only ever mounted by a caller that has already checked
 * `hasAnyChapterImage(track.chapters)` — a story with no chapter images
 * must see the plain `<Cover>` this replaces, with no wrapper and no
 * layout change. That gating lives in the pages (Listen.tsx, KidMode.tsx),
 * not here.
 *
 * Tapping the illustration opens `<ChapterSceneFullscreen>` (product ask:
 * children want to look at the details) — `onExpand` is optional so this
 * component still works as a plain, non-interactive illustration for a
 * caller that doesn't wire up the full-screen view. The whole thing is one
 * `<button>` rather than a tap zone layered over the image: simplest way
 * to get a real, keyboard-reachable control with a single aria-label,
 * and the "agrandir" badge is purely decorative on top of it.
 */
import { forwardRef } from "react";
import { Maximize2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Cover } from "./Cover";
import { useChapterSceneLayers } from "./chapterSceneCrossfade";
import {
  currentSceneChapter, nextSceneChapter, sceneImageSrc, toSceneChapters,
} from "../lib/chapterScenes";
import type { AudioTrack } from "../lib/audioPlayer";

export const ChapterScene = forwardRef<HTMLButtonElement, {
  track: AudioTrack;
  currentTime: number;
  className?: string;
  rounded?: string;
  /** Opens the full-screen view. Omit to render a static, non-interactive
   *  illustration (no button semantics, no "agrandir" badge). */
  onExpand?: () => void;
}>(function ChapterScene({ track, currentTime, className = "", rounded = "rounded-2xl", onExpand }, ref) {
  const { t } = useTranslation();
  const sceneChapters = toSceneChapters(track.chapters);
  const scene = currentSceneChapter(sceneChapters, currentTime);
  const next = nextSceneChapter(sceneChapters, currentTime);
  const targetKey = scene ? sceneImageSrc(scene) : null;

  const layers = useChapterSceneLayers(targetKey);
  const preloadSrc = next ? sceneImageSrc(next) : null;

  const visual = (
    // `aspect-square` here too, not just on the layers: every layer below
    // is `absolute inset-0` (needed so the crossfading ones stack exactly
    // on top of each other), which takes all of them out of flow — without
    // an explicit size on this wrapper itself the container would collapse
    // to zero height.
    <div className={`relative aspect-square w-full ${rounded} ${className}`}>
      {layers.map((layer, i) => {
        const isTop = i === layers.length - 1;
        return (
          <div
            key={layer.key ?? "__cover__"}
            className={`absolute inset-0 motion-safe:transition-opacity motion-safe:ease-out ${
              isTop && !layer.entered ? "opacity-0" : "opacity-100"
            }`}
            style={{ transitionDuration: "600ms" }}
          >
            {layer.key ? (
              <div className={`overflow-hidden ${rounded} border border-line bg-surface aspect-square`}>
                <img
                  src={layer.key}
                  alt={track.title}
                  className="block w-full h-full object-cover"
                />
              </div>
            ) : (
              <Cover
                svg={track.coverSvg}
                imageUrl={track.coverImageUrl}
                imageThumbUrl={track.coverImageThumbUrl}
                imageSize="full"
                title={track.title}
                colorway={track.coverColorway}
                colorKey={track.id}
                rounded={rounded}
              />
            )}
          </div>
        );
      })}
      {onExpand && (
        <span
          aria-hidden="true"
          className="absolute bottom-2 right-2 w-8 h-8 rounded-full bg-night/70 flex items-center justify-center pointer-events-none"
        >
          <Maximize2 size={15} className="text-paper" />
        </span>
      )}
      {/* Offscreen, not display:none: an image with no box never starts
       *  its network fetch in some browsers. 1x1 keeps it out of layout
       *  and off screen without disabling the decode. */}
      {preloadSrc && (
        <img
          src={preloadSrc}
          alt=""
          aria-hidden="true"
          width={1}
          height={1}
          className="absolute w-px h-px opacity-0 pointer-events-none -z-10"
        />
      )}
    </div>
  );

  if (!onExpand) return visual;

  return (
    <button
      type="button"
      ref={ref}
      onClick={onExpand}
      aria-label={t("player.chapterSceneExpand")}
      className="block w-full text-left"
    >
      {visual}
    </button>
  );
});
